import { isValidElement, useState, type ReactElement } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import MuiSnackbar from '@mui/material/Snackbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { SxProps, Theme } from '@mui/material/styles';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import Icon from 'shared/atoms/Icon';
import T from 'shared/atoms/Typography';

import theme from 'shared/themes/default';

import HideAfterCounter from './components/HideAfterCounter';

const ContentWrapper = styled.div`
  align-items: center;
  display: flex;
  width: 100%;
`;

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1.25rem;
`;

interface TitleWrapperProps {
  isTablet?: boolean;
}

const TitleWrapper = styled.div<TitleWrapperProps>`
  align-items: center;
  display: flex;
  flex-grow: 1;
  gap: ${theme.spacing.m1};
  padding: ${(props) =>
    props.isTablet ? `${theme.spacing.m1} 0` : '1.25rem 0'};
`;

const ActionWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

interface ButtonWrapperProps {
  hasCounter?: boolean;
  isTablet?: boolean;
}

const ButtonWrapper = styled.div<ButtonWrapperProps>`
  align-items: center;
  display: flex;
  gap: ${theme.spacing.m1};
  margin-right: 0;
  padding-top: ${(props) => (props.hasCounter ? '1.125rem' : '0')};
  padding-right: ${(props) =>
    props.isTablet ? theme.spacing.m1 : theme.spacing.m3};
  padding-left: ${(props) =>
    props.isTablet ? theme.spacing.m1 : theme.spacing.m3};
`;

interface DetailedError {
  title: string;
  description?: string;
  element?: ReactElement;
}

interface SnackbarPosition {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
}

interface HideAfterConfig {
  desktop?: number;
  mobile?: number;
}

interface SnackbarProps {
  detailedErrors?: DetailedError[];
  hideAfter?: HideAfterConfig;
  message: string;
  onClose: () => void;
  open: boolean;
  position?: SnackbarPosition;
  top?: number;
  type: 'generic' | 'success' | 'warning' | 'error';
}

const Snackbar = ({
  detailedErrors,
  hideAfter = {},
  message,
  onClose,
  open,
  position = {
    vertical: 'top',
    horizontal: 'center',
  },
  top,
  type,
}: SnackbarProps) => {
  const [expanded, setExpanded] = useState(false);

  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.mobile);
  const isTablet = useMediaQuery(theme.breakpoints.tablet);
  const { vertical, horizontal } = position;

  const sx: SxProps<Theme> = {
    width: '100%',
    top:
      top === undefined
        ? `${theme.sizes.header} !important`
        : `${top}px !important`,
    left: isMobile ? '0' : 'none',
    color: theme.palette.mainDark,
    '& .MuiSnackbarContent-root': {
      alignItems: 'start',
      borderRadius: '0 0 0.375rem 0.375rem',
      color: theme.palette.mainDark,
      padding: isTablet
        ? `0 0 0 ${theme.spacing.m1}`
        : `0 0 0 ${theme.spacing.m3}`,
      width: '100%',
    },
    '& .MuiSnackbarContent-message': {
      display: 'flex',
      flexDirection: 'column',
      padding: '0',
      width: '100%',
    },
  };

  const buttonSx = {
    cursor: 'pointer',
  };

  const countdownValue = isMobile
    ? hideAfter.mobile || 5
    : hideAfter.desktop || 10;

  const backgroundColorMap: Record<
    'generic' | 'success' | 'warning' | 'error',
    string
  > = {
    generic: theme.palette.lightDistinct,
    success: theme.palette.successTinted,
    warning: theme.palette.warningTinted,
    error: theme.palette.errorTinted,
  };

  const iconColorMap: Record<
    'generic' | 'success' | 'warning' | 'error',
    string
  > = {
    generic: theme.palette.mainDark,
    success: theme.palette.successDark,
    warning: theme.palette.mainDark,
    error: theme.palette.errorDark,
  };

  const backgroundColor = backgroundColorMap[type];
  const iconColor = iconColorMap[type];

  sx['& .MuiSnackbarContent-root'] = {
    ...(sx['& .MuiSnackbarContent-root'] as object),
    backgroundColor,
  };

  const hasCounter = type !== 'error';

  return (
    <MuiSnackbar
      anchorOrigin={{ vertical, horizontal }}
      key={vertical + horizontal}
      message={
        <>
          <ContentWrapper>
            <TitleWrapper isTablet={isTablet}>
              <Icon
                icon={
                  type === 'success' || type === 'generic'
                    ? CheckCircleIcon
                    : ErrorOutlineIcon
                }
                color={iconColor}
              />
              <T
                color={theme.palette.mainDark}
                variant={isMobile ? 'paragraph' : 'subtitle'}
                fontWeight="semibold"
              >
                {`${message}${
                  detailedErrors
                    ? ` (${t('generic.errorCount', {
                        count: detailedErrors.length,
                      })})`
                    : ''
                }`}
              </T>
            </TitleWrapper>
            <ActionWrapper>
              <ButtonWrapper isTablet={isTablet} hasCounter={hasCounter}>
                {detailedErrors &&
                  (expanded ? (
                    <ExpandLess
                      onClick={() => setExpanded(false)}
                      sx={buttonSx}
                    />
                  ) : (
                    <ExpandMore
                      onClick={() => setExpanded(true)}
                      sx={buttonSx}
                    />
                  ))}
                <CloseIcon onClick={onClose} sx={buttonSx} />
              </ButtonWrapper>
              {hasCounter && (
                <HideAfterCounter
                  startFrom={countdownValue}
                  onComplete={onClose}
                />
              )}
            </ActionWrapper>
          </ContentWrapper>
          {expanded && detailedErrors && (
            <DetailsWrapper>
              {detailedErrors.map(({ title, description, element }) => (
                <div key={title}>
                  <T color={theme.palette.mainDark}>
                    <T
                      color={theme.palette.mainDark}
                      component="span"
                      fontWeight="bold"
                    >{`${title}:`}</T>
                    {description ? ` ${description}` : ''}
                  </T>
                  {!description && isValidElement(element) ? element : null}
                </div>
              ))}
            </DetailsWrapper>
          )}
        </>
      }
      open={open}
      onClose={onClose}
      sx={sx}
    />
  );
};

export default Snackbar;
export type { SnackbarProps, DetailedError };
